import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";

const credentials = [
  "Onafhankelijke advisering — geen leveranciersbelangen, geen softwareverkoop",
  "Bestuurlijke vertaling van AI, cloud, algoritmen en datagovernance",
  "Aansluiting bij Europese kaders zoals AI verordening, AVG, NIS2 en BIO",
  "Ervaring met publieke besluitvorming, raadsprocessen en digitale zaken",
];

export default function Over() {
  return (
    <div data-testid="page-over" className="pt-[96px]">
      <SeoHead
        title="Over SovEU — onze missie"
        description="Een digitaal onafhankelijk Europa is geen ideaal. Het is een keuze. SovEU helpt organisaties van compliance naar controle, van afhankelijkheid naar regie."
      />
      {/* Hero */}
      <section className="section bg-white">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Over SovEU</div>
            <h1 className="h1-display mb-6">
              Een digitaal onafhankelijk Europa is geen ideaal. Het is een keuze.
            </h1>
            <p className="body-lg text-[#334155]">
              SovEU is opgericht om publieke, semipublieke en maatschappelijke organisaties te helpen die keuze concreet te maken: van compliance naar controle, van afhankelijkheid naar regie.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Mission */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="over-mission"
      >
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <div className="eyebrow mb-3">Onze missie</div>
            <h2 className="h2-display mb-7">
              Van compliance naar controle. Van afhankelijkheid naar regie.
            </h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              Wij geloven dat bestuurlijke regie over digitale systemen niet begint bij grote woorden, maar bij praktische vragen. Welke systemen gebruiken wij? Welke data verwerken wij? Welke algoritmen ondersteunen besluiten? Welke leveranciers bepalen onze ruimte om te handelen? En kunnen wij dit uitleggen aan burgers, huurders, studenten, cliënten, toezichthouders en bestuur?
            </p>
            <p>
              Wij helpen organisaties die vragen scherp te krijgen en te vertalen naar overzicht, governance en bestuurlijke regie. Niet door angst te verkopen, maar door inzicht en uitvoerbare keuzes te organiseren. Publieke data verdient publieke verantwoordelijkheid. AI gebruik verdient uitlegbaarheid. Digitale afhankelijkheid verdient bewuste besluitvorming.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Origin & credentials */}
      <section className="section bg-white" data-testid="over-origin">
        <div className="container-soveu grid lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-7 space-y-5 text-[#334155] body-lg leading-relaxed">
            <div className="eyebrow mb-3">Onze achtergrond</div>
            <h2 className="h2-display mb-2" style={{ color: "#1E3A5F" }}>
              Bestuurlijk én technisch onderlegd
            </h2>
            <p>
              SovEU is opgericht door <strong className="text-[#1E3A5F]">Achraf Ait Daoua</strong>, voormalig gemeenteraadslid met digitale zaken in zijn portefeuille en met een achtergrond in cybersecurity. Vanuit die combinatie kennen wij zowel de bestuurlijke logica van raad, college en directie, als de technische werkelijkheid van AI, cloud en datasystemen.
            </p>
            <p>
              Die dubbele bril vertalen wij naar adviezen die staan: technisch correct, juridisch zorgvuldig en bestuurlijk bruikbaar. Wij schrijven niet voor specialisten, maar voor de mensen die uiteindelijk verantwoording moeten afleggen.
            </p>
            <p>
              Wij werken bewust onafhankelijk. Geen softwareverkoop, geen leveranciersbelangen — alleen een heldere beoordeling en concrete vervolgstappen. Onze ambitie is praktisch: organisaties helpen om aantoonbaar regie te voeren over hun digitale werkelijkheid.
            </p>
          </FadeIn>

          <FadeIn delay={150} className="lg:col-span-5">
            <aside className="soveu-card p-8" data-testid="over-credentials">
              <div className="eyebrow mb-4">Onze positie</div>
              <ul className="space-y-4">
                {credentials.map((c, i) => (
                  <li
                    key={c}
                    className="flex items-start gap-3 text-[#1A1A1A]"
                    data-testid={`credential-${i + 1}`}
                  >
                    <BadgeCheck
                      size={20}
                      className="mt-0.5 flex-shrink-0"
                      style={{ color: "#2D6F8F" }}
                      strokeWidth={1.75}
                    />
                    <span className="leading-relaxed text-[15px]">{c}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#E5E9EF]">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: "#1E3A5F" }}
                  data-testid="over-contact-cta"
                >
                  Maak kennis met SovEU
                  <ArrowRight size={16} />
                </Link>
              </div>
            </aside>
          </FadeIn>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="over-closing"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">
              Wilt u kennismaken?
            </h2>
            <p className="body-lg text-white/75 mb-8">
              Plan een kort gesprek over uw vraagstuk. Wij denken vrijblijvend mee over de stap die voor uw organisatie logisch is.
            </p>
            <Link to="/contact" className="btn-primary btn-on-navy" data-testid="over-final-cta">
              Plan een gesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
